import { format, formatDuration, intervalToDuration, parse, set, Duration } from 'date-fns';
import formatNumber from './numberFormatHelpers';
import { LOADING_STATE, TimeZone } from '@/state/general/generalTypes';
import { toZonedTime } from 'date-fns-tz';
import { convertSecondsToDuration } from '@/lib/helpers/durationHelpers';
import { SortingTable } from '@/lib/types/generalTypes';

export { formatNumber };

export const getDateFormattedDate = (date, formatStr) => {
    try {
        return format(new Date(date), formatStr);
    } catch (e) {
        return formatStr;
    }
};

export const pathMatcher = (path: string | null | undefined, url: string | undefined): boolean => {
    if (typeof path !== 'string') return false;
    if (typeof url !== 'string') return false;

    if (path === url) return true;
    return path?.includes(url);
};
export const getRandomNumber = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const nFormatter = (number, isUsd = false, options: { fraction?: number } = {}) => {
    if (typeof number !== 'number') return 'N/A';

    const num = Math.abs(number);
    const sign = number >= 0 ? '' : '-';
    const { fraction } = options;
    const getFraction = (numericValue: number): number => {
        const toFormat = Math.abs(numericValue);
        const intPart = `${toFormat}`.split('.');
        const length = intPart[0].length;
        if (length >= 3) return 0;
        return Math.abs(length - 3);
    };

    const formatB = 1000_000_000;
    const formatM = 1000_000;
    const formatK = 1000;

    if (num >= formatB) {
        const valueToFormat = num / formatB;
        return (
            sign +
            (isUsd ? '$' : '') +
            formatNumber(valueToFormat, fraction || getFraction(valueToFormat)).replace(
                /\.0$/,
                '',
            ) +
            'B'
        );
    }
    if (num >= formatM) {
        const valueToFormat = num / formatM;
        return (
            sign +
            (isUsd ? '$' : '') +
            formatNumber(valueToFormat, fraction || getFraction(valueToFormat)).replace(
                /\.0$/,
                '',
            ) +
            'M'
        );
    }
    if (num >= formatK) {
        const valueToFormat = num / formatK;

        return (
            sign +
            (isUsd ? '$' : '') +
            formatNumber(valueToFormat, getFraction(valueToFormat)).replace(/\.0$/, '') +
            'K'
        );
    }

    return (
        sign +
        (isUsd ? '$' : '') +
        formatNumber(num, fraction || getFraction(num)).replace(/\.0$/, '')
    );
};

export function range(start: number, stop: number, step: number): number[] {
    if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
        return [];
    }

    const result: number[] = [];
    for (let i = start; step > 0 ? i < stop : i > stop; i += step) {
        result.push(i);
    }

    return result;
}

export const findFraction = (
    num: number | string,
    initialFraction: number = 5,
    maxFraction?: number,
): number => {
    if (num === 0 || num === null || num === undefined || isNaN(+num)) return initialFraction;
    const stringValue = Math.abs(+num).toString();
    let fraction = initialFraction;

    const parts = stringValue.split('.');
    const fractional = parts?.[1] || null;
    const isScientific = stringValue.includes('e');

    if (fractional) {
        let index = -1;
        for (let i = 0; i < fractional.length; i++) {
            if (fractional[i] !== '0') {
                index = i;
                break;
            }
        }
        if (index + 1 > initialFraction) {
            fraction = index + 1;
        }
    }
    if (isScientific) {
        const coef = stringValue.split('e')[1];
        const coefValue = Math.abs(Number(coef));
        if (coefValue > 4) {
            fraction = coefValue + 1;
        }
    }

    if (fraction > 12) {
        fraction = 12;
    }

    if (maxFraction && fraction > maxFraction) {
        return maxFraction;
    }

    return fraction;
};

export const sortTableData = (
    data: any[],
    sortBy: SortingTable,
    disableCaseSensitive?: boolean,
) => {
    const dataForSort = [...data];

    dataForSort.sort((a, b) => {
        const firstValue =
            a && a?.original?.[sortBy.column] !== undefined ? a.original[sortBy.column] : -1;
        const secondValue =
            b && b?.original?.[sortBy.column] !== undefined ? b.original[sortBy.column] : -1;

        if (!isNaN(+firstValue) && !isNaN(+secondValue)) {
            if (+firstValue < +secondValue) {
                return sortBy.direction === 'asc' ? -1 : 1;
            }
            if (+firstValue > +secondValue) {
                return sortBy.direction === 'asc' ? 1 : -1;
            }
            return 0;
        } else {
            let backupFirst = firstValue;
            let backupSecond = secondValue;

            if (disableCaseSensitive) {
                backupFirst = firstValue ? firstValue.toString().toLowerCase() : '';
                backupSecond = secondValue ? secondValue.toString().toLowerCase() : '';
            }

            if (backupFirst < backupSecond) {
                return sortBy.direction === 'asc' ? -1 : 1;
            }
            if (backupFirst > backupSecond) {
                return sortBy.direction === 'asc' ? 1 : -1;
            }
            return 0;
        }
    });

    return dataForSort;
};

export const getDateDifferenceInDays = (date1: Date | string, date2: Date | string) => {
    if (!date1 || !date2) return -1;
    const diffTime = Math.abs(+new Date(date2) - +new Date(date1));
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
export const extractError = (error: any) => {
    const message = {
        message: '', // error message
        statusCode: null,
        error: '', // title
        reason: '',
        errorCode: '',
    };

    message.message = error?.response?.data?.message || 'Something went wrong';
    message.statusCode = error?.response?.data?.statusCode || null;
    message.error = error?.response?.data?.error || 'Error';
    message.reason = error?.response?.data?.reason || '';
    message.errorCode = error?.response?.data?.errorCode || '';

    return message;
};

export const randomString = (length: number): string => {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
};

export const jsonParser = (json: string, valueOnReject: any = null) => {
    try {
        return json ? JSON.parse(json) : valueOnReject;
    } catch (e) {
        return valueOnReject;
    }
};

export const sliceIntoChunks = (arr: any[], chunkSize: number): any[][] => {
    const res: any[][] = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        const chunk: any[] = arr.slice(i, i + chunkSize);
        res.push(chunk);
    }
    return res;
};

export const formatDurationString = (
    update: string,
    t: any,
    locale?: any,
    disablePostfix?: boolean,
): string => {
    const now = new Date();
    let startEnd = {
        start: new Date(update),
        end: now,
    };

    if (disablePostfix) {
        startEnd = {
            start: now,
            end: new Date(update),
        };
    }

    const interval = intervalToDuration(startEnd);
    if (!interval) return 'N/A';

    let durationFormatter: (keyof Duration)[] = ['minutes'];
    // @ts-ignore
    if (interval?.months > 0) {
        durationFormatter = ['months', 'days', 'hours', 'minutes'];
        // @ts-ignore
    } else if (interval?.days > 0) {
        durationFormatter = ['days', 'hours', 'minutes'];
        // @ts-ignore
    } else if (interval?.hours > 0) {
        durationFormatter = ['hours', 'minutes'];
        // @ts-ignore
    } else if (interval?.minutes > 0) {
        durationFormatter = ['minutes'];
        // @ts-ignore
    } else if (interval?.seconds > 0) {
        durationFormatter = ['seconds'];
    }

    const formattedDuration = formatDuration(interval, { format: durationFormatter, locale });
    return formattedDuration
        ? `${formattedDuration} ${!disablePostfix ? t('duration:ago') : ''}`
        : t('duration:now');
};

export const getLabeledTimezones = (
    timezones: TimeZone[],
): {
    label: string;
    value: string;
    offset: number;
}[] => {
    const labeled = timezones.map((timezone) => {
        const hour = Math.abs(parseInt(`${timezone.offset / 60}`, 10));
        const minute = Math.abs(timezone.offset % 60);
        let sign = timezone.offset < 0 ? '+' : '-';
        if (timezone.offset === 0) {
            sign = '';
        }

        const hourString = hour < 10 ? `0${hour}` : `${hour}`;
        const minuteString = minute < 10 ? `0${minute}` : `${minute}`;

        const label = `(UTC ${sign}${hourString}:${minuteString}) ${timezone.name}`;
        return {
            label,
            value: timezone.name,
            offset: timezone.offset,
        };
    });

    labeled.sort((a, b) => {
        return a.label.localeCompare(b.label);
    });

    return labeled;
};
export const convertDateToSelectedTimezone = (date: Date, timezone: string): Date => {
    return toZonedTime(new Date(new Date(date).toUTCString()), timezone || '');
};

export const removeEmptyStringValues = (obj) => {
    const result = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            if (obj[key] !== '') {
                result[key] = obj[key];
            }
        }
    }

    return result;
};

export const isValidDate = (date) => {
    // @ts-ignore
    return date instanceof Date && !isNaN(date) ? date : 0;
};

export const createFixedDate = (dateStr: string): Date => {
    if (!dateStr) return new Date();
    // Parse the string as if it is in the local timezone
    let date = parse(dateStr, 'yyyy-MM-dd', new Date());
    // Ensure the time is exactly midnight in local time
    date = set(date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

    return date;
};

export const formatDate = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const toSnakeCase = (text: string): string => {
    return text
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .replace(/\s+/g, '_')
        .replace(/-+/g, '_')
        .toLowerCase();
};

export const calculateProgress = (
    entry: number,
    live: number,
    liquidation: number,
    direction: 'long' | 'short',
) => {
    if ((direction === 'short' && live < entry) || (direction === 'long' && live > entry)) {
        return 0;
    }

    let progress = 0;

    if (direction === 'short') {
        progress = ((live - entry) / (liquidation - entry)) * 100;
    } else {
        progress = ((entry - live) / (entry - liquidation)) * 100;
    }

    return Math.max(0, Math.min(100, progress));
};

export const shortenString = (str: string, start = 6, end = 3): string => {
    if (str?.length > 10) {
        return str?.substring(0, start) + '...' + str?.substring(str?.length - end);
    }
    return str;
};

export const shortenDisplayName = (str: string, symbols = 7): string => {
    if (str?.length > 7) {
        return str?.substring(0, symbols) + '.';
    }
    return str;
};

export const getSignForNFormatted = (value) => {
    return value > 0 ? '+' : '';
};
export const getSignForFormattedNumber = (value) => {
    if (value === 0) return '';
    return value > 0 ? '+' : '-';
};

export const getFormatNumberStyle = (valueNow: number, valuePast: number) => {
    if (valueNow > valuePast) return { sign: '+', color: 'success.main' };
    if (valueNow < valuePast) return { sign: '-', color: 'error.main' };
    return { sign: '', color: 'text.primary' };
};

export const getNFormatterStyle = (valueNow: number, valuePast: number) => {
    if (valueNow > valuePast) return { sign: '+', color: 'success.main' };
    if (valueNow < valuePast) return { sign: '', color: 'error.main' };
    return { sign: '', color: 'text.primary' };
};

export const getDigits = (midPx: string, szDecimals: number, maxDecimals: number): number => {
    if (midPx === undefined || midPx === null) {
        return 0;
    }
    const significantDigits = 5;
    if (Number(midPx) === 0) {
        if (szDecimals >= maxDecimals) {
            return Math.min(significantDigits, maxDecimals);
        } else {
            return Math.min(significantDigits, maxDecimals - szDecimals);
        }
    } else if (Number(midPx) < 1) {
        let precision: number;

        if (szDecimals >= maxDecimals) {
            precision = Math.min(significantDigits, maxDecimals);
        } else {
            precision = Math.min(significantDigits, maxDecimals - szDecimals);
        }

        const pricePrecized = Number(midPx).toPrecision(precision);
        const [_, decimalPart = ''] = pricePrecized.toString().split('.');
        return Math.min(maxDecimals - szDecimals, decimalPart.length);
    } else {
        let precision: number;

        if (szDecimals >= maxDecimals) {
            precision = Math.max(significantDigits, maxDecimals);
        } else {
            precision = Math.max(significantDigits, maxDecimals - szDecimals);
        }

        const pricePrecized = Number(midPx).toPrecision(precision);
        const [integerPart, decimalPart = ''] = pricePrecized.toString().split('.');
        // tslint:disable-next-line:variable-name
        const _significantDigits = Math.max(significantDigits, integerPart.length);
        return Math.min(maxDecimals - szDecimals, _significantDigits - integerPart.length);
    }
};
